import {
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { PuntajesCleaver } from './types.js';

// Misma presentación que MotorHardcodeado.html (referencia validada por Vero):
// tarjetas por perfil con puntaje y percentil, y la gráfica de perfil DISC
// con las 3 líneas sobre percentiles. Sin filas de suma: cada valor se lee
// por sí solo.

type Factor = 'D' | 'I' | 'S' | 'C';
type Perfil = 'M' | 'L' | 'T';

const FACTORES: ReadonlyArray<{ key: Factor; label: string; color: string }> = [
  { key: 'D', label: 'Dominancia',   color: '#C84B31' },
  { key: 'I', label: 'Influencia',   color: '#B8820A' },
  { key: 'S', label: 'Estabilidad',  color: '#3A7D5E' },
  { key: 'C', label: 'Cumplimiento', color: '#3B5FA0' },
];

const PERFILES: ReadonlyArray<{
  key: Perfil;
  titulo: string;
  serie: string;
  color: string;
  dash?: string;
  labelPos: 'top' | 'bottom' | 'right';
}> = [
  { key: 'M', titulo: 'Perfil MÁS (M) — Motivación',     serie: 'Más (M)',   color: '#2C4A3E', labelPos: 'top' },
  { key: 'L', titulo: 'Perfil MENOS (L) — Presión', serie: 'Menos (L)', color: '#8B3A2C', dash: '6 4', labelPos: 'bottom' },
  { key: 'T', titulo: 'Perfil TOTAL (T) — Normal',  serie: 'Total (T)', color: '#1A3A80', dash: '3 3', labelPos: 'right' },
];

function fmtPuntaje(perfil: Perfil, valor: number): string {
  return perfil === 'T' && valor > 0 ? `+${valor}` : String(valor);
}

function TarjetasPerfil({ perfil, titulo, puntajes }: { perfil: Perfil; titulo: string; puntajes: PuntajesCleaver }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>
        {titulo}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
        {FACTORES.map(({ key, label, color }) => (
          <div
            key={key}
            style={{
              border: '1px solid var(--border)',
              borderTop: `4px solid ${color}`,
              borderRadius: 10,
              padding: '10px 8px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color }}>
              {key} — {label}
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color, lineHeight: 1.2 }}>
              {fmtPuntaje(perfil, puntajes[perfil][key])}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>
              Percentil <strong style={{ color: 'var(--heading)' }}>{puntajes.percentiles[perfil][key]}</strong>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EjeFactor({ x, y, payload }: { x?: number; y?: number; payload?: { value: Factor } }) {
  const factor = FACTORES.find((f) => f.key === payload?.value);
  if (!factor) return null;
  return (
    <g transform={`translate(${x},${y})`}>
      <text dy={14} textAnchor="middle" fill={factor.color} fontSize={13} fontWeight={700}>{factor.key}</text>
      <text dy={28} textAnchor="middle" fill="#7A7068" fontSize={10}>{factor.label}</text>
    </g>
  );
}

function GraficaPerfilDisc({ puntajes }: { puntajes: PuntajesCleaver }) {
  const data = FACTORES.map(({ key }) => ({
    factor: key,
    ...Object.fromEntries(PERFILES.map((p) => [p.serie, puntajes.percentiles[p.key][key]])),
  }));

  return (
    <ResponsiveContainer width="100%" height={340}>
      <LineChart data={data} margin={{ top: 20, right: 28, left: 0, bottom: 8 }}>
        <CartesianGrid stroke="#E0DAD0" vertical={false} />
        <XAxis dataKey="factor" tick={<EjeFactor />} height={40} padding={{ left: 40, right: 40 }} />
        <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={{ fontSize: 11 }} width={36} />
        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {PERFILES.map((p) => (
          <Line
            key={p.key}
            type="linear"
            dataKey={p.serie}
            stroke={p.color}
            strokeWidth={p.dash ? 2 : 2.5}
            strokeDasharray={p.dash}
            dot={{ r: p.dash ? 4 : 5, fill: p.color, strokeWidth: 0 }}
            isAnimationActive={false}
          >
            <LabelList dataKey={p.serie} position={p.labelPos} fill={p.color} fontSize={11} fontWeight={600} />
          </Line>
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function CleaverResultado({ puntajes }: { puntajes: PuntajesCleaver }) {
  return (
    <>
      {PERFILES.map((p) => (
        <TarjetasPerfil key={p.key} perfil={p.key} titulo={p.titulo} puntajes={puntajes} />
      ))}
      <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--heading)', margin: '8px 0 4px' }}>
        Gráfica de Perfil DISC <span style={{ fontWeight: 400, color: 'var(--muted)' }}>· Percentiles</span>
      </h3>
      <GraficaPerfilDisc puntajes={puntajes} />
    </>
  );
}
